import { MessageSquare, FileText, Lightbulb, Wrench, ClipboardCheck } from 'lucide-react';
import { useSlideIn } from '../hooks/useSlideIn';

const steps = [
  {
    number: '01',
    icon: MessageSquare,
    title: 'Beratung',
    description: 'Kostenlose und unverbindliche Erstberatung. Wir analysieren Ihre Anforderungen und besprechen Ihre Wünsche.',
  },
  {
    number: '02',
    icon: FileText,
    title: 'Angebot',
    description: 'Transparentes und detailliertes Angebot mit fairer Preisgestaltung. Keine versteckten Kosten.',
  },
  {
    number: '03',
    icon: Lightbulb,
    title: 'Planung',
    description: 'Professionelle Projektplanung mit modernsten Technologien. Termintreue und Qualität garantiert.',
  },
  {
    number: '04',
    icon: Wrench,
    title: 'Ausführung',
    description: 'Fachgerechte Umsetzung durch qualifiziertes Personal. Sauber, präzise und normgerecht.',
  },
  {
    number: '05',
    icon: ClipboardCheck,
    title: 'Abnahme',
    description: 'Gemeinsame Abnahme und Dokumentation. Wir erklären Ihnen alle Funktionen Ihrer neuen Anlage.',
  },
];

export const Process = () => {
  const anim = useSlideIn();
  return (
    <section id="ablauf" data-testid="process-section" className="py-20 md:py-32 bg-white overflow-hidden">
      <div ref={anim.ref} className={"max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 slide-in-right" + (anim.isVisible ? " visible" : "")}>
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-[#E31E24] font-semibold text-sm uppercase tracking-widest mb-4">
            So arbeiten wir
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 mb-6">
            In 5 Schritten zum Ziel
          </h2>
          <p className="text-neutral-600 text-base lg:text-lg max-w-2xl mx-auto">
            Vertrauen durch Transparenz – unser strukturierter Ablauf garantiert Ihnen einen reibungslosen Projektverlauf.
          </p>
        </div>

        {/* Process Steps - Desktop */}
        <div className="hidden lg:block relative">
          {/* Connection Line */}
          <div className="absolute top-24 left-0 right-0 h-0.5 bg-zinc-200" />
          <div className="absolute top-24 left-0 w-1/2 h-0.5 bg-[#E31E24]" />

          <div className="grid grid-cols-5 gap-6">
            {steps.map((step, index) => {
              const IconComponent = step.icon;
              return (
                <div 
                  key={index}
                  data-testid={`process-step-${index + 1}`}
                  className="relative text-center group"
                >
                  {/* Large Number Background */}
                  <div className="absolute -top-8 left-1/2 -translate-x-1/2 text-[8rem] font-bold text-zinc-100 leading-none select-none pointer-events-none z-0">
                    {step.number}
                  </div>
                  
                  {/* Icon Circle */}
                  <div className="relative z-10 w-16 h-16 mx-auto bg-white border-2 border-zinc-200 flex items-center justify-center group-hover:border-[#E31E24] group-hover:bg-[#E31E24] transition-all duration-300">
                    <IconComponent className="w-7 h-7 text-[#E31E24] group-hover:text-white transition-colors" />
                  </div>
                  
                  {/* Content */}
                  <div className="mt-6 relative z-10">
                    <h3 className="text-lg font-bold text-neutral-900 mb-2">
                      {step.title}
                    </h3>
                    <p className="text-sm text-neutral-600 leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Process Steps - Mobile */}
        <div className="lg:hidden space-y-8">
          {steps.map((step, index) => {
            const IconComponent = step.icon;
            return (
              <div 
                key={index}
                data-testid={`process-step-mobile-${index + 1}`}
                className="relative flex gap-6"
              >
                {/* Number & Icon */}
                <div className="flex-shrink-0">
                  <div className="relative">
                    <span className="absolute -top-4 -left-2 text-6xl font-bold text-zinc-100 select-none">
                      {step.number}
                    </span>
                    <div className="relative z-10 w-14 h-14 bg-white border-2 border-[#E31E24] flex items-center justify-center">
                      <IconComponent className="w-6 h-6 text-[#E31E24]" />
                    </div>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="w-0.5 h-16 bg-zinc-200 ml-7 mt-2" />
                  )}
                </div>
                
                {/* Content */}
                <div className="pt-2">
                  <h3 className="text-lg font-bold text-neutral-900 mb-2">
                    {step.title}
                  </h3>
                  <p className="text-sm text-neutral-600 leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Process;
